import { Dialog, Transition } from '@headlessui/react';
import { useState, useEffect } from 'react';
import { Fragment } from 'react';
import QRCode from 'react-qr-code';
import moment from 'moment';
import { http } from '../../config/http';
import {
  IconCopy,
  IconCheck,
  IconHistory,
  IconInfoCircle,
  IconX,
  IconBuildingStore,
  IconUser,
  IconShieldCheck,
  IconClock,
  IconArrowRight,
  IconTruck,
  IconTrash,
  IconCirclePlus,
  IconShoppingCart,
  IconMapPin,
  IconBan
} from '@tabler/icons-react';
import ImageMagnifier from '../../components/common/ImageMagnifier';
import { resolveItemImageUrl } from '../../utils/itemImage';

export default function DetailItem({ closeModalDetail, openDetail, detailItem }) {
  const [openImageModal, setOpenImageModal] = useState(false);
  const [activeTab, setActiveTab] = useState('detail');
  const [historyTimeline, setHistoryTimeline] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [summaryDates, setSummaryDates] = useState(null);

  useEffect(() => {
    if (openDetail && detailItem?.id) {
      setActiveTab('detail');
      setSummaryDates(null);
      fetchHistory();
    }
  }, [openDetail, detailItem]);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await http.get(`/item/${detailItem.id}/history`);
      if (response.data.status) {
        setHistoryTimeline(response.data.data);
        if (response.data.summary_dates) {
          setSummaryDates(response.data.summary_dates);
        }
      }
    } catch (error) {
      console.error("Failed to fetch history", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  function numberWithCommas(x) {
    if (x === null || x === undefined || x === '') return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function openLah() {
    closeModalDetail();
  }
  const [copy, setCopy] = useState(false);

  const handleCopy = (kodeItem) => {
    navigator.clipboard.writeText(kodeItem);
    setCopy(true);
    setTimeout(() => {
      setCopy(false);
    }, 3000);
  };

  const formatTanggal = (val) => {
    if (!val) return null;
    if (typeof val === 'string' && val.includes('/') && val.length <= 16) return val;
    const m = moment(val);
    return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : val;
  };

  // 1. Tanggal Input (INP) - data tanggal kapan barang diinput
  const rawInput =
    summaryDates?.tanggal_input ||
    detailItem?.tanggal_input ||
    detailItem?.created_at ||
    null;
  const tglInput = formatTanggal(rawInput) || '-';

  // 2. Tanggal Pindah ke Brankas Pusat (approval=PST)
  // Data tanggal kapan barang masuk ke pusat (saat di approval). Kalau belum approval, data kosong (-)
  const approvedPusatFromTimeline = historyTimeline.find(
    (h) => (h.stage === 'pindah_pusat') &&
           (h.status === 'Disetujui Masuk Pusat' || h.type === 'transfer_approved')
  );
  const rawPusat =
    summaryDates?.tanggal_masuk_pusat ||
    approvedPusatFromTimeline?.tgl_approval ||
    null;
  const tglPusat = formatTanggal(rawPusat);

  // 3. Tanggal Toko (Posisi terakhir tanggal ke toko terakhir / saat ini)
  // Data tanggal kapan barang masuk ke etalase (saat di approval). Kalau belum approval, data kosong (-)
  const receivedTokoEvents = historyTimeline.filter(
    (h) => (h.stage === 'pengiriman_toko' || h.stage === 'pindah_antar_toko') &&
           (h.status === 'Diterima di Toko' || h.status === 'Pindah Disetujui' || h.type === 'receive' || h.type === 'transfer_approved')
  );
  const lastReceivedToko = receivedTokoEvents.length > 0 ? receivedTokoEvents[receivedTokoEvents.length - 1] : null;

  const rawToko =
    summaryDates?.tanggal_toko ||
    lastReceivedToko?.tgl_approval ||
    null;
  const tglToko = formatTanggal(rawToko);

  const namaTokoTerakhir =
    tglToko
      ? (summaryDates?.nama_toko_terakhir || lastReceivedToko?.lokasi_tujuan || null)
      : null;

  const imgSrc = resolveItemImageUrl(detailItem);

  return (
    <>
      <Transition appear show={openDetail} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={() => { }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          {/* Full Image Preview Lightbox */}
          {openImageModal && openDetail && imgSrc && (
            <div
              onClick={() => setOpenImageModal(false)}
              className="fixed inset-0 z-[1100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
            >
              <button
                type="button"
                onClick={() => setOpenImageModal(false)}
                className="absolute top-5 right-5 p-2 bg-slate-800/80 hover:bg-slate-700 text-white rounded-full transition shadow-lg cursor-pointer"
              >
                <IconX size={24} />
              </button>
              <img
                src={imgSrc}
                alt="Item Preview Full"
                className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center p-4 min-h-[440px]">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden bg-white dark:bg-slate-900 px-5 sm:px-6 pt-12 sm:pt-6 pb-5 text-left align-middle shadow-xl transition-all sm:rounded-xl relative">
                  {/* Absolute Close Button for better accessibility */}
                  <button
                    onClick={openLah}
                    className="absolute top-3 right-3 p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-all z-10"
                    title="Tutup"
                  >
                    <IconX size={24} stroke={2.5} />
                  </button>

                  {/* Header Section */}
                  <div className="flex flex-col sm:flex-row items-start justify-between border-b border-amber-100/60 dark:border-slate-800 pb-4 mb-5 gap-4">
                    <div className="flex-1 w-full min-w-0">
                      <div className="flex gap-3 items-center font-bold text-xl sm:text-2xl text-slate-800 dark:text-white mb-2 pr-10 sm:pr-0">
                        {detailItem?.jenis_kategori?.nama_jenis}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">KODE BARANG:</span>
                        <div className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded border border-amber-200 dark:border-amber-800/50">
                          <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
                            {detailItem?.kode_item}
                          </span>
                          {copy ? (
                            <IconCheck className="w-4 h-4 text-green-500 stroke-2" />
                          ) : (
                            <button onClick={() => handleCopy(detailItem?.kode_item)} className="text-slate-400 hover:text-amber-500 transition-colors">
                              <IconCopy className="w-4 h-4 stroke-1.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Ringkasan 3 Tanggal Siklus Barang: INP, PST, TKO */}
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 text-slate-400 dark:text-slate-500 text-[10px] sm:text-xs flex-wrap mt-2">
                        {/* 1. Tanggal Input (INP) */}
                        <div className="bg-slate-50 dark:bg-slate-800/60 px-2.5 py-1.5 rounded-lg border border-slate-200/70 dark:border-slate-700/60 min-w-[125px]">
                          <p className="text-slate-400 dark:text-slate-500 text-[10px] mb-0.5 font-medium">Tanggal Input</p>
                          <p className="text-slate-700 dark:text-slate-200 font-semibold flex items-center">
                            <span>{tglInput || '-'}</span>
                          </p>
                        </div>

                        {/* 2. Tanggal Pindah ke Brankas Pusat (approval=PST) */}
                        <div className="bg-amber-50/40 dark:bg-amber-950/20 px-2.5 py-1.5 rounded-lg border border-amber-200/70 dark:border-amber-800/40 min-w-[135px]">
                          <p className="text-slate-400 dark:text-slate-500 text-[10px] mb-0.5 font-medium">Masuk Pusat</p>
                          <p className="text-amber-800 dark:text-amber-300 font-semibold flex items-center">
                            <span>{tglPusat || '-'}</span>
                          </p>
                        </div>

                        {/* 3. Tanggal Toko (Posisi terakhir tanggal ke toko terakhir / saat ini) */}
                        <div className="bg-blue-50/40 dark:bg-blue-950/20 px-2.5 py-1.5 rounded-lg border border-blue-200/70 dark:border-blue-800/40 min-w-[135px]">
                          <p className="text-slate-400 dark:text-slate-500 text-[10px] mb-0.5 font-medium">
                            Tanggal Toko {namaTokoTerakhir ? <span className="font-bold text-blue-600 dark:text-blue-400">({namaTokoTerakhir})</span> : ''}
                          </p>
                          <p className="text-blue-800 dark:text-blue-300 font-semibold flex items-center">
                            <span>{tglToko || '-'}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* <div className="flex items-center gap-3 shrink-0 self-end sm:self-start">
                      <div className="text-center bg-white p-1.5 rounded-lg border border-slate-200 shadow-sm">
                        <QRCode
                          value={detailItem?.kode_item === undefined ? 'unkwow' : detailItem?.kode_item}
                          size={56}
                        />
                      </div>
                      {detailItem?.gambar1?.[0]?.asli ? (
                        <div className="relative group rounded-lg overflow-hidden border-2 border-amber-200 dark:border-amber-700/50 bg-white dark:bg-slate-900 shadow-sm transition-all hover:border-amber-400 hover:shadow-md cursor-zoom-in">
                          {openDetail && (
                            <PhotoView src={`${imageBaseUrl}${detailItem?.gambar1[0]?.asli}`}>
                              <img
                                className="w-[56px] h-[56px] sm:w-[64px] sm:h-[64px] object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                src={`${imageBaseUrl}${detailItem?.gambar1[0]?.asli}`}
                                alt="Foto Barang"
                              />
                            </PhotoView>
                          )}
                        </div>
                      ) : (
                        <div className="w-[56px] h-[56px] rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 text-[9px] text-center px-1">
                          No Image
                        </div>
                      )}
                    </div> */}
                  </div>

                  {/* Details Section */}
                  <div className="space-y-4">
                    {/* Tabs */}
                    <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700">
                      <button
                        onClick={() => setActiveTab('detail')}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                          activeTab === 'detail'
                            ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                            : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                        }`}
                      >
                        Rincian
                      </button>
                      <button
                        onClick={() => setActiveTab('history')}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                          activeTab === 'history'
                            ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                            : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                        }`}
                      >
                        Perjalanan Barang
                      </button>
                    </div>

                    {activeTab === 'detail' ? (
                      <>
                        {/* Section Foto dengan Kaca Pembesar (Magnifying Glass Lens) */}
                        {imgSrc && (
                          <div className="mb-4">
                            <ImageMagnifier
                              src={imgSrc}
                              // alt={detailItem?.jenis_kategori?.nama_jenis || detailItem?.kode_item || 'Foto Barang'}
                              zoomLevel={3}
                              lensSize={130}
                              maxHeight="300px"
                              // badgeTitle={detailItem?.jenis_kategori?.nama_jenis || 'Perhiasan Emas'}
                              onPreviewClick={() => setOpenImageModal(true)}
                            />
                          </div>
                        )}

                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200/60 dark:border-slate-700/60 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-200/60 dark:border-slate-700/60 bg-white/50 dark:bg-slate-800/80">
                        <h2 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                          <span className="w-1.5 h-4 bg-amber-500 rounded-full"></span>
                          Rincian Barang
                        </h2>
                      </div>
                      <div className="divide-y divide-slate-200/60 dark:divide-slate-700/60">
                        <div className="px-4 py-2 flex text-sm items-center justify-between hover:bg-white/40 dark:hover:bg-slate-800/40 transition-colors">
                          <span className="text-slate-500 dark:text-slate-400 font-medium">Berat</span>
                          <span className="font-bold text-amber-600 dark:text-amber-400 text-base">{detailItem?.berat} <span className="text-sm font-semibold ml-0.5">Gram</span></span>
                        </div>

                        <div className="px-4 py-2 flex text-sm items-center justify-between hover:bg-white/40 dark:hover:bg-slate-800/40 transition-colors">
                          <span className="text-slate-500 dark:text-slate-400 font-medium">Kadar Emas</span>
                          <span className="font-bold text-amber-600 dark:text-amber-400 text-base">{detailItem?.karat} <span className="text-sm font-semibold ml-0.5">Karat</span></span>
                        </div>

                        <div className="px-4 py-2 flex text-sm items-center justify-between hover:bg-white/40 dark:hover:bg-slate-800/40 transition-colors">
                          <span className="text-slate-500 dark:text-slate-400 font-medium">Harga Modal / gr</span>
                          <span className="font-bold text-slate-800 dark:text-slate-200">Rp {numberWithCommas(detailItem?.harga_modal === undefined ? '0' : detailItem?.harga_modal)}</span>
                        </div>

                        <div className="px-4 py-2 flex text-sm items-center justify-between hover:bg-white/40 dark:hover:bg-slate-800/40 transition-colors">
                          <span className="text-slate-500 dark:text-slate-400 font-medium">Lokasi</span>
                          <span className="font-semibold text-slate-700 dark:text-slate-300 bg-slate-200/50 dark:bg-slate-700/50 px-2.5 py-1 rounded border border-slate-300/50 dark:border-slate-600">
                            {detailItem?.cabang?.nama_cabang}
                          </span>
                        </div>

                        {/* <div className="px-4 py-3 flex text-sm items-center justify-between hover:bg-white/40 dark:hover:bg-slate-800/40 transition-colors">
                          <span className="text-slate-500 dark:text-slate-400 font-medium">Status</span>
                          {detailItem?.is_ready === true ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-semibold border border-green-200 dark:border-green-800/30">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                              Tersedia
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-semibold border border-red-200 dark:border-red-800/30">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                              Tidak Tersedia
                            </span>
                          )}
                        </div> */}
                      </div>
                    </div>

                    {/* Transfer History Table */}
                    {!detailItem?.history_transfer?.length ? '' : (
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200/60 dark:border-slate-700/60 overflow-hidden mt-4">
                        <div className="px-4 py-3 border-b border-slate-200/60 dark:border-slate-700/60 bg-white/50 dark:bg-slate-800/80">
                          <h2 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                            <span className="w-1.5 h-4 bg-slate-400 dark:bg-slate-500 rounded-full"></span>
                            Riwayat Transfer Lokasi
                          </h2>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                            <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-100/50 dark:bg-slate-800/60 border-b border-slate-200/60 dark:border-slate-700/60">
                              <tr>
                                <th scope="col" className="px-4 py-2.5 font-semibold text-center w-12">No</th>
                                <th scope="col" className="px-4 py-2.5 font-semibold">Toko Asal</th>
                                <th scope="col" className="px-4 py-2.5 font-semibold">Toko Tujuan</th>
                                <th scope="col" className="px-4 py-2.5 font-semibold text-right">Tanggal Pindah</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                              {detailItem?.history_transfer?.map((ht, i) => (
                                <tr key={i} className="hover:bg-white/60 dark:hover:bg-slate-800/40 transition-colors">
                                  <td className="px-4 py-2.5 text-center text-slate-400 dark:text-slate-500">{i + 1}.</td>
                                  <td className="px-4 py-2.5 font-medium">{ht.asal_cabang.nama_cabang}</td>
                                  <td className="px-4 py-2.5 font-medium text-amber-700 dark:text-amber-400">{ht.tujuan_cabang.nama_cabang}</td>
                                  <td className="px-4 py-2.5 text-right text-slate-500 dark:text-slate-400">{ht.created_at}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {/* <div className="border-b mt-1 text-base font-normal text-slate-600 flex justify-between px-5">
                        Histori Barang
                        <div className="text-gray-900">
                          <div className="rounded-sm text-green-500 pb-2">
                            {detailItem?.history_transfer?.map((ht, i) => (
                              <div key={i}>
                                <div className="flex items-center justify-between">
                                  <div>Asal Cabang :</div>
                                  <div>{ht.asal_cabang.nama_cabang}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div> */}
                    {detailItem?.keterangan && (
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200/60 dark:border-slate-700/60 p-4 mt-4">
                        <div>
                          <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                            Keterangan
                          </h3>
                          <p className="text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-3 rounded-lg leading-relaxed">
                            {detailItem?.keterangan}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 mt-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-3">
                        <div className="text-center bg-white p-1.5 rounded-lg border border-slate-200 shadow-sm shrink-0">
                          <QRCode
                            value={detailItem?.kode_item === undefined ? 'unkwow' : detailItem?.kode_item}
                            size={56}
                          />
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          <p className="font-bold text-slate-700 dark:text-slate-200">QR Code Label</p>
                          <p className="font-mono text-[11px] text-amber-600 dark:text-amber-400 font-bold">{detailItem?.kode_item || '-'}</p>
                        </div>
                      </div>
                    </div>
                      </>
                    ) : (
                      /* History Tab */
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200/60 dark:border-slate-700/60 p-4 mt-4 relative max-h-[460px] overflow-y-auto">
                        {loadingHistory ? (
                          <div className="flex flex-col items-center justify-center p-8 space-y-3">
                            <div className="animate-spin border-4 border-slate-200 border-t-amber-500 rounded-full w-9 h-9"></div>
                            <span className="text-xs text-slate-500 font-medium">Memuat perjalanan barang...</span>
                          </div>
                        ) : historyTimeline.length === 0 ? (
                          <div className="text-center text-slate-500 py-10 flex flex-col items-center">
                            <IconHistory size={40} className="text-slate-300 dark:text-slate-600 mb-2 stroke-[1.5]" />
                            <p className="font-semibold text-slate-600 dark:text-slate-400 text-sm">Belum ada riwayat perjalanan</p>
                            <p className="text-xs text-slate-400 mt-0.5">Barang belum memiliki rekam jejak mutasi</p>
                          </div>
                        ) : (
                          <div>
                            {/* Summary Header */}
                            <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-200/80 dark:border-slate-700/80">
                              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                Rekam Jejak Alur Barang
                              </span>
                              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40">
                                {historyTimeline.length} Tahap
                              </span>
                            </div>

                            {/* Timeline Items */}
                            <div className="relative border-l-2 border-slate-200 dark:border-slate-700 ml-3.5 space-y-5">
                              {historyTimeline.map((item, index) => {
                                const isInput = item.stage === 'input' || item.type === 'input';
                                const isPindahPusat = item.stage === 'pindah_pusat' || item.type?.startsWith('transfer');
                                const isKirimToko = item.stage === 'pengiriman_toko' || item.type === 'transfer' || item.type === 'receive' || item.type?.startsWith('delivery');
                                const isHapus = item.stage === 'hapus_barang' || item.type?.startsWith('delete');
                                const isJual = item.stage === 'penjualan' || item.type === 'sold' || item.type === 'refund';

                                const isRejected = item.type?.includes('rejected') || item.status_color === 'red' || item.status?.toLowerCase().includes('ditolak');
                                const isCancelled = item.type?.includes('cancelled') || item.status_color === 'rose' || item.status?.toLowerCase().includes('dibatalkan');

                                return (
                                  <div key={index} className="relative pl-6">
                                    {/* Icon / Bullet Marker */}
                                    <div className={`absolute -left-[13px] top-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white dark:bg-slate-900 ${
                                      isCancelled
                                        ? 'border-rose-500 text-rose-600 bg-rose-50/80'
                                        : isRejected
                                        ? 'border-red-500 text-red-600 bg-red-50/80'
                                        : isInput
                                        ? 'border-blue-500 text-blue-600 bg-blue-50/80'
                                        : isPindahPusat
                                        ? 'border-purple-500 text-purple-600 bg-purple-50/80'
                                        : isKirimToko
                                        ? 'border-amber-500 text-amber-600 bg-amber-50/80'
                                        : isHapus
                                        ? 'border-rose-500 text-rose-600 bg-rose-50/80'
                                        : isJual
                                        ? 'border-emerald-500 text-emerald-600 bg-emerald-50/80'
                                        : 'border-slate-400 text-slate-500 bg-slate-50/80'
                                    }`}>
                                      {isCancelled ? (
                                        <IconBan size={14} className="stroke-[2.5]" />
                                      ) : isRejected ? (
                                        <IconX size={14} className="stroke-[2.5]" />
                                      ) : isInput ? (
                                        <IconCirclePlus size={14} className="stroke-[2.5]" />
                                      ) : isPindahPusat ? (
                                        <IconBuildingStore size={14} className="stroke-[2.5]" />
                                      ) : isKirimToko ? (
                                        <IconTruck size={14} className="stroke-[2.5]" />
                                      ) : isHapus ? (
                                        <IconTrash size={14} className="stroke-[2.5]" />
                                      ) : isJual ? (
                                        <IconShoppingCart size={14} className="stroke-[2.5]" />
                                      ) : (
                                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                                      )}
                                    </div>

                                    {/* Event Card */}
                                    <div className="bg-white dark:bg-slate-800/90 rounded-xl p-3.5 border border-slate-200/80 dark:border-slate-700/80 shadow-xs hover:shadow-sm transition-all">
                                      {/* Header: Title & Badges */}
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <div>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                              {item.title}
                                            </span>
                                            {item.no_referensi && item.no_referensi !== '-' && (
                                              <span className="text-[11px] font-mono font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                                                {item.no_referensi}
                                              </span>
                                            )}
                                          </div>
                                          <span className="text-[11px] text-slate-400 dark:text-slate-500 flex items-center gap-1 mt-0.5 font-medium">
                                            <IconClock size={13} /> {item.date}
                                          </span>
                                        </div>

                                        {/* Status Badge */}
                                        {item.status && (
                                          <span className={`shrink-0 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${
                                            item.status_color === 'green'
                                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800'
                                              : item.status_color === 'blue'
                                              ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800'
                                              : (item.status_color === 'red' || item.status_color === 'rose')
                                              ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800'
                                              : item.status_color === 'amber'
                                              ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800'
                                              : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                                          }`}>
                                            {item.status}
                                          </span>
                                        )}
                                      </div>

                                      {/* Detail Grid */}
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-100 dark:border-slate-700/60">
                                        {/* Lokasi / Rute */}
                                        {(item.lokasi || item.lokasi_asal) && (
                                          <div className="flex items-start gap-1.5 col-span-1 sm:col-span-2 bg-slate-50/80 dark:bg-slate-900/40 px-2.5 py-1.5 rounded-lg border border-slate-100 dark:border-slate-800">
                                            {/* <IconMapPin size={15} className="text-amber-500 shrink-0 mt-0.5" /> */}
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span className="font-semibold text-slate-500 dark:text-slate-400">Lokasi:</span>
                                              {item.lokasi_asal && item.lokasi_tujuan ? (
                                                <span className="font-medium text-slate-800 dark:text-slate-200 inline-flex items-center gap-1">
                                                  <span className="font-semibold text-slate-700 dark:text-slate-300">{item.lokasi_asal}</span>
                                                  <IconArrowRight size={13} className="text-slate-400" />
                                                  <span className="font-bold text-amber-600 dark:text-amber-400">{item.lokasi_tujuan}</span>
                                                </span>
                                              ) : (
                                                <span className="font-bold text-slate-700 dark:text-slate-200">{item.lokasi || item.lokasi_asal}</span>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Pemohon / Pengirim / Diinput Oleh */}
                                        <div className="flex items-start gap-1.5 p-2 rounded-lg bg-slate-50/60 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50">
                                          {/* <IconUser size={15} className="text-blue-500 shrink-0 mt-0.5" /> */}
                                          <div className="flex flex-col">
                                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider">
                                              {isInput ? 'Diinput Oleh' :
                                               isPindahPusat ? 'Pemohon (Pindah Pusat)' :
                                               isKirimToko ? 'Pengirim (Pembuat SJ)' :
                                               isHapus ? 'Dihapus / Diajukan Oleh' :
                                               'User Terkait'}
                                            </span>
                                            <span className="font-semibold text-slate-800 dark:text-slate-200 text-xs">
                                              {item.pemohon || item.user || '-'}
                                            </span>
                                            {item.tgl_pindah && !isInput && (
                                              <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                                                {item.tgl_pindah}
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        {/* Verifikator / Approval */}
                                        {(isPindahPusat || isKirimToko || isHapus) && (
                                          <div className="flex items-start gap-1.5 p-2 rounded-lg bg-slate-50/60 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50">
                                            <IconShieldCheck size={15} className={`shrink-0 mt-0.5 ${
                                              item.approval ? (isRejected || isCancelled ? 'text-rose-500' : 'text-emerald-500') : 'text-slate-400'
                                            }`} />
                                            <div className="flex flex-col">
                                              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider">
                                                {isCancelled
                                                  ? 'Dibatalkan Oleh'
                                                  : isRejected
                                                  ? (isPindahPusat ? 'Ditolak Pusat Oleh' : isKirimToko ? 'Ditolak Toko Oleh' : 'Ditolak Oleh')
                                                  : isKirimToko
                                                  ? 'Penerima / Approval Toko'
                                                  : 'Verifikator / Approval'}
                                              </span>
                                              <span className="font-semibold text-slate-800 dark:text-slate-200 text-xs">
                                                {item.approval ? (
                                                  item.approval
                                                ) : (
                                                  <span className="text-amber-500 italic font-normal">Menunggu Persetujuan</span>
                                                )}
                                              </span>
                                              {item.tgl_approval && (
                                                <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                                                  {isCancelled ? 'Batal' : isRejected ? 'Ditolak' : 'Approve'}: {item.tgl_approval}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Kurir jika ada */}
                                        {item.kurir && (
                                          <div className="col-span-1 sm:col-span-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50/70 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/40">
                                            {/* <IconTruck size={15} className="shrink-0" /> */}
                                            <span className="font-medium text-xs">Kurir Pengantar: <strong className="font-bold">{item.kurir}</strong></span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Alasan / Catatan */}
                                      {(item.alasan || item.catatan) && (
                                        <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-700/50 text-xs text-slate-600 dark:text-slate-400 space-y-1 bg-slate-50/40 dark:bg-slate-900/30 p-2 rounded-lg">
                                          {item.alasan && (
                                            <div className="flex items-start gap-1">
                                              <span className="font-semibold text-slate-500 dark:text-slate-400 shrink-0">Alasan/Catatan:</span>
                                              <span className="italic">{item.alasan}</span>
                                            </div>
                                          )}
                                          {item.catatan && (
                                            <div className="flex items-start gap-1">
                                              <span className="font-semibold text-slate-500 dark:text-slate-400 shrink-0">
                                                {isCancelled ? 'Catatan Pembatalan:' : isRejected ? 'Catatan Penolakan:' : 'Catatan Approval:'}
                                              </span>
                                              <span className="italic">{item.catatan}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={openLah}
                      className="px-6 py-2.5 rounded-lg font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-300 transition-colors shadow-sm"
                    >
                      Tutup
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
